/**
 * role-utils — Utility functions for role-based access control (RBAC).
 * issue_139: Frontend role-based access control for organization profiles.
 * issue_163: Updated USER → CREATOR role semantics. USER is now a "pending" role.
 *
 * These functions accept the user's role as a string (from user.role)
 * and return a boolean indicating whether the action is permitted.
 */

import { UserRole } from '@flashcard/shared';

/**
 * Check if the given role is ORGANIZATION_ADMIN.
 */
export function isOrganizationAdmin(role: string | undefined): boolean {
  return role === UserRole.ORGANIZATION_ADMIN;
}

/**
 * Check if the given role is ORGANIZATION_MEMBER.
 */
export function isOrganizationMember(role: string | undefined): boolean {
  return role === UserRole.ORGANIZATION_MEMBER;
}

/**
 * Check if the given role is an organization profile (admin or member).
 */
export function isOrganizationProfile(role: string | undefined): boolean {
  return isOrganizationAdmin(role) || isOrganizationMember(role);
}

/**
 * Check if the user with the given role can create content (decks, flashcards, collections).
 * Only CREATOR and ADMIN roles can create content.
 * (issue_163: changed from USER to CREATOR — USER is now a pending role)
 */
export function canCreateContent(role: string | undefined): boolean {
  return role === UserRole.CREATOR || role === UserRole.ADMIN;
}

/**
 * Check if the user with the given role can import/export data.
 * Only CREATOR and ADMIN roles can import/export.
 * (issue_163: changed from USER to CREATOR — USER is now a pending role)
 */
export function canImportExport(role: string | undefined): boolean {
  return role === UserRole.CREATOR || role === UserRole.ADMIN;
}

/**
 * Check if the given role is a CREATOR or ADMIN (full-access individual profile).
 * (issue_163: new function — replaces the old implicit "is regular user" check)
 */
export function isCreator(role: string | undefined): boolean {
  return role === UserRole.CREATOR || role === UserRole.ADMIN;
}

/**
 * Check if the given role is a pending USER (newly registered, onboarding not yet completed).
 * (issue_163: new function — USER is now a "pending" role that must choose an onboarding path)
 */
export function isPendingUser(role: string | undefined): boolean {
  return role === UserRole.USER;
}